import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  OverlayView,
  DirectionsRenderer,
} from "@react-google-maps/api";
import config from "../../config.js";
import { CircularProgress, Typography } from "@mui/material";
import BusIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import ScreenRouteVehicleStatusTable from "./ScreenRouteVehicleStatusTable";
import { useSettings } from "../../context/SettingsContext";
import { useMapIcons } from "../../hooks/useMapIcons.js";

const styles = {
  container: {
    padding: "32px",
  },
  cardSection: {
    marginBottom: "32px",
  },
  card: {
    padding: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: "12px",
    color: "white",
  },
  vehicleCard: {
    backgroundColor: "rgba(88, 0, 191, 1)",
  },
  stopCard: {
    backgroundColor: "rgba(0, 178, 191, 1)",
  },
  scheduleCard: {
    backgroundColor: "rgba(191, 80, 0, 1)",
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
  },
  icon: {
    fontSize: "40px",
    marginTop: "10px",
  },
  mapContainer: {
    width: "100%",
    height: "100vh",
    marginBottom: "32px",
  },
  tableContainer: {
    marginTop: "32px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "-7px",
  },
  tableHeader: {
    padding: "8px",
    textAlign: "left",
    backgroundColor: "#f5f5f5",
  },
  tableCell: {
    border: "1px solid #ddd",
    padding: "8px",
  },
  noData: {
    textAlign: "center",
    padding: "16px",
  },
  formHeader: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    marginBottom: "16px",
  },
};

const mapStyles = [
  {
    featureType: "all",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];

const libraries = ["geometry"];

const colors = [
  "#20A8E0",
  "#8FBC8F",
  "#FFD700",
  "#6495ED",
  "#FF69B4",
  "#FFA07A",
  "#ADFF2F",
  "#FF4500",
  "#9370DB",
  "#FFFFFF",
];

const normalizeDeviceData = (data) => {
  if (data.deviceId && data.latitude && data.longitude) {
    return data;
  }
  return {
    deviceId: data.device_id,
    latitude: data.Latitude,
    longitude: data.Longitude,
    timestamp: new Date().toISOString(),
    rawData: data.RawData || "",
  };
};

const calculateDistanceAndETA = (
  vehiclePos,
  routePath,
  stops,
  vehicleSpeed,
) => {
  if (!routePath || routePath.length === 0) return { distance: 0, eta: 0 };

  const vehiclePoint = new window.google.maps.LatLng(
    vehiclePos.lat,
    vehiclePos.lng,
  );
  let minDistance = Infinity;
  let closestIndex = 0;

  routePath.forEach((point, index) => {
    const distance =
      window.google.maps.geometry.spherical.computeDistanceBetween(
        vehiclePoint,
        point,
      );
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  const remainingPath = routePath.slice(closestIndex);
  const totalDistance =
    remainingPath.reduce((acc, point, i) => {
      if (i === 0) return acc;
      return (
        acc +
        window.google.maps.geometry.spherical.computeDistanceBetween(
          remainingPath[i - 1],
          point,
        )
      );
    }, 0) / 1000; // Convert to km

  const avgSpeed = vehicleSpeed || 10; // Default speed in km/h if not available
  const timeInSeconds = (totalDistance / avgSpeed) * 3600; // Convert to seconds

  let cumulativeDuration = 0;
  stops.forEach((stop, index) => {
    if (index >= closestIndex) {
      cumulativeDuration += parseInt(stop.durationValue || 60); // Default 1 min if not available
    }
  });

  return {
    distance: totalDistance.toFixed(2),
    eta: timeInSeconds + cumulativeDuration,
  };
};

const RouteScreen = () => {
  const location = useLocation();
  const { routeId } = useParams();
  const routeData = location.state?.selectedRouteData;
  const [vehicleData, setVehicleData] = useState({});
  const [mapLoaded, setMapLoaded] = useState(false);
  const [directions, setDirections] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const routePathRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [labelPositions, setLabelPositions] = useState({});
  const [vehicleIds, setVehicleIds] = useState([]);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [vehicleName, setVehicleName] = useState({});
  const eventSourceRef = useRef(null);
  const [filteredStops, setFilteredStops] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [EtaAndDistance, setEtaAndDistance] = useState([]);

  const { settings } = useSettings();
  const { getOriginStopIcon, getDestinationStopIcon, getIntermediateStopIcon } =
    useMapIcons(
      settings,
      routeData?.vehicles || [],
      routeData?.stops || [],
      routeData ? [routeData] : [],
    );

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey || "YOUR_API_KEY_HERE",
    libraries,
  });

  const fetchAllVehicleLocations = useCallback(() => {
    if (vehicleIds.length === 0) return;

    const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    const sseUrl = `${config.baseURL}/redis/stream?${query}`;
    const source = new EventSource(sseUrl);
    eventSourceRef.current = source;

    source.onopen = () => {
      console.log("Persistent SSE connection established");
    };

    source.onerror = (error) => {
      console.error("Persistent SSE error:", error);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setTimeout(fetchAllVehicleLocations, 5000);
    };

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type && data.type === "connected") {
          console.log("Received connection confirmation:", data);
          return;
        }
        if (data.device_id && data.Latitude && data.Longitude) {
          const normalizedData = normalizeDeviceData(data);
          setVehicleLocations((prev) => {
            const existingIndex = prev.findIndex(
              (item) => item.deviceId === normalizedData.deviceId,
            );
            if (existingIndex >= 0) {
              const newLocations = [...prev];
              newLocations[existingIndex] = normalizedData;
              return newLocations;
            }
            return [...prev, normalizedData];
          });
        } else if (!Array.isArray(data)) {
          setVehicleLocations((prev) => {
            const existingIndex = prev.findIndex(
              (item) => item.deviceId === data.deviceId,
            );
            if (existingIndex >= 0) {
              const newLocations = [...prev];
              newLocations[existingIndex] = data;
              return newLocations;
            }
            return [...prev, data];
          });
        } else if (data.locations && Array.isArray(data.locations)) {
          setVehicleLocations((prev) => {
            const newLocations = [...prev];
            data.locations.forEach((location) => {
              const existingIndex = newLocations.findIndex(
                (item) => item.deviceId === location.deviceId,
              );
              if (existingIndex >= 0) {
                newLocations[existingIndex] = location;
              } else {
                newLocations.push(location);
              }
            });
            return newLocations;
          });
        } else if (Array.isArray(data)) {
          const newLocations = [];
          data.forEach((location) => {
            if (location.device_id && location.Latitude && location.Longitude) {
              newLocations.push(normalizeDeviceData(location));
            } else if (
              location.deviceId &&
              location.latitude &&
              location.longitude
            ) {
              newLocations.push(location);
            }
          });
          if (newLocations.length > 0) {
            setVehicleLocations(newLocations);
          }
        } else {
          console.warn("Received SSE data in unexpected format:", data);
        }
      } catch (e) {
        console.error("Error parsing SSE data:", e);
      }
    };
  }, [vehicleIds]);

  useEffect(() => {
    if (vehicleIds.length > 0) {
      fetchAllVehicleLocations();
      return () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
      };
    }
  }, [vehicleIds, fetchAllVehicleLocations]);

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return hash;
  };

  const getBackgroundColor = (deviceId) => {
    const index = Math.abs(hashString(deviceId)) % colors.length;
    return colors[index];
  };

  const center = useMemo(() => {
    if (routeData?.routeCoordinates?.[0]) {
      return {
        lat: parseFloat(routeData.routeCoordinates[0].latitude),
        lng: parseFloat(routeData.routeCoordinates[0].longitude),
      };
    }
    if (routeData?.stops?.[0]) {
      return {
        lat: parseFloat(routeData.stops[0].latitude),
        lng: parseFloat(routeData.stops[0].longitude),
      };
    }
    return { lat: 0, lng: 0 };
  }, [routeData]);

  const stopMarkers = useMemo(() => {
    return routeData
      ? routeData.stops.map((stop) => ({
          position: {
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          },
          label: stop.name,
        }))
      : [];
  }, [routeData]);

  const calculateRouteDirections = useCallback(() => {
    if (!window.google || !routeData?.routeCoordinates?.length || !mapInstance)
      return;
    const directionsService = new window.google.maps.DirectionsService();
    const origin = new window.google.maps.LatLng(
      parseFloat(routeData.routeCoordinates[0].latitude),
      parseFloat(routeData.routeCoordinates[0].longitude),
    );
    const destination = new window.google.maps.LatLng(
      parseFloat(
        routeData.routeCoordinates[routeData.routeCoordinates.length - 1]
          .latitude,
      ),
      parseFloat(
        routeData.routeCoordinates[routeData.routeCoordinates.length - 1]
          .longitude,
      ),
    );
    const waypoints = routeData.routeCoordinates.slice(1, -1).map((coord) => ({
      location: new window.google.maps.LatLng(
        parseFloat(coord.latitude),
        parseFloat(coord.longitude),
      ),
      stopover: coord.isStop || false,
    }));
    directionsService.route(
      {
        origin,
        destination,
        waypoints,
        optimizeWaypoints: false,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          console.log("Directions received:", result);
          setDirections({ ...result, routeColor: routeData.routeColor });
          routePathRef.current = result.routes[0].overview_path;
        } else {
          console.error(
            `Error fetching directions for route ${routeId}: ${status}`,
          );
          setError(`Error fetching directions: ${status}`);
        }
      },
    );
  }, [routeData, mapInstance, routeId]);

  const onLoad = useCallback((map) => {
    setMapLoaded(true);
    setMapInstance(map);
  }, []);

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return "N/A";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatDistance = (distance) => {
    if (!distance) return "N/A";
    return `${parseFloat(distance).toFixed(1)} km`;
  };

  const calculateLabelPositions = useCallback((vehicles) => {
    const positions = {};
    const LABEL_HEIGHT = 30;
    const groupedVehicles = [];
    vehicles.forEach((vehicle) => {
      if (!vehicle || !vehicle.position) return;
      let foundGroup = false;
      for (const group of groupedVehicles) {
        const firstVehicle = group[0];
        const distance =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            new window.google.maps.LatLng(
              vehicle.position.lat,
              vehicle.position.lng,
            ),
            new window.google.maps.LatLng(
              firstVehicle.position.lat,
              firstVehicle.position.lng,
            ),
          );
        if (distance < 50) {
          group.push(vehicle);
          foundGroup = true;
          break;
        }
      }
      if (!foundGroup) groupedVehicles.push([vehicle]);
    });
    groupedVehicles.forEach((group, groupIndex) => {
      group.forEach((vehicle, index) => {
        positions[vehicle.device_id] = LABEL_HEIGHT * (groupIndex + index);
      });
    });
    return positions;
  }, []);

  useEffect(() => {
    if (routeData?.vehicles?.length > 0) {
      const ids = routeData.vehicles.map((vehicle) => vehicle.device_id);
      console.log("Vehicle IDs:", ids);
      setVehicleIds(ids);
    }
  }, [routeData]);

  useEffect(() => {
    const updatedVehicleData = vehicleLocations.reduce((acc, location) => {
      if (location.deviceId && location.latitude && location.longitude) {
        const position = {
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude),
        };
        const { distance, eta } = calculateDistanceAndETA(
          position,
          routePathRef.current,
          routeData?.stops || [],
          location.speed || 0,
        );
        acc[location.deviceId] = {
          device_id: location.deviceId,
          position,
          timestamp: location.timestamp,
          distance,
          eta,
        };
      }
      return acc;
    }, {});
    setVehicleData(updatedVehicleData);
    setLastUpdateTime(new Date().toLocaleString());
    setLoading(false);
    if (Object.values(updatedVehicleData).length > 0) {
      const newPositions = calculateLabelPositions(
        Object.values(updatedVehicleData),
      );
      setLabelPositions(newPositions);
    }
  }, [vehicleLocations, routeData?.stops, calculateLabelPositions]);

  useEffect(() => {
    if (isLoaded && routeData?.routeCoordinates?.length > 0 && mapInstance) {
      calculateRouteDirections();
    }
  }, [isLoaded, routeData, mapInstance, calculateRouteDirections]);

  useEffect(() => {
    const vehicleName = {};
    if (routeData?.vehicles?.length > 0) {
      routeData?.vehicles?.forEach((v) => {
        if (!vehicleName[v?.device_id]) {
          vehicleName[v?.device_id] = v?.name;
        }
      });
      setVehicleName(vehicleName);
    }
  }, [routeData]);

  if (!routeData) {
    return (
      <div style={styles.noData}>
        <Typography variant="h6">No route data available</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.noData}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={styles.noData}>
        <Typography color="error">Failed to load Google Maps</Typography>
        <Typography color="error">{mapError}</Typography>
        <button onClick={() => window.location.reload()}>Refresh</button>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={styles.noData}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        gap: "20px",
        width: "100vw",
        padding: "16px",
        height: "100vh",
      }}
    >
      <div style={{ width: "71%" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "95.5vh" }}
          center={center}
          zoom={18}
          onLoad={onLoad}
          options={{
            styles: mapStyles,
            disableDefaultUI: true,
            zoomControl: false,
            streetViewControl: true,
            fullscreenControl: true,
            mapTypeControl: false,
            gestureHandling: "greedy",
            minZoom: 15,
            maxZoom: 60,
          }}
        >
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: {
                  strokeColor: directions.routeColor || "#0000FF",
                  strokeOpacity: 0.8,
                  strokeWeight: 3,
                },
              }}
            />
          )}
          {stopMarkers.map((marker, index) => (
            <React.Fragment key={index}>
              <OverlayView
                position={marker.position}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  style={{ position: "relative", top: "-62px", left: "0px" }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "white",
                      color:
                        index === 0
                          ? "green"
                          : index === stopMarkers.length - 1
                            ? "blue"
                            : "black",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize:
                        index === 0 || index === stopMarkers.length - 1
                          ? "12px"
                          : "10px",
                      fontWeight:
                        index === 0 || index === stopMarkers.length - 1
                          ? "700"
                          : "500",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      whiteSpace: "nowrap",
                      zIndex: 1000,
                    }}
                  >
                    {marker.label}
                  </div>
                </div>
              </OverlayView>
              <Marker
                position={marker.position}
                icon={(() => {
                  const isOrigin = index === 0;
                  const isDestination = index === stopMarkers.length - 1;
                  let customIcon = null;
                  if (isOrigin) customIcon = getOriginStopIcon();
                  else if (isDestination) customIcon = getDestinationStopIcon();
                  else customIcon = getIntermediateStopIcon();
                  return (
                    customIcon ||
                    (isOrigin
                      ? {
                          url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
                          scaledSize: new window.google.maps.Size(40, 40),
                        }
                      : isDestination
                        ? {
                            url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                            scaledSize: new window.google.maps.Size(40, 40),
                          }
                        : undefined)
                  );
                })()}
              />
            </React.Fragment>
          ))}
          {Array.isArray(vehicleLocations) &&
            vehicleLocations.map(
              (vehicle) =>
                vehicle &&
                (vehicle.latitude || vehicle.Latitude) &&
                (vehicle.longitude || vehicle.Longitude) && (
                  <React.Fragment key={vehicle.deviceId || vehicle.device_id}>
                    <OverlayView
                      position={{
                        lat: parseFloat(vehicle.latitude || vehicle.Latitude),
                        lng: parseFloat(vehicle.longitude || vehicle.Longitude),
                      }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        style={{
                          position: "relative",
                          top: "-90px",
                          left: "0px",
                          width: "2px",
                          height: "2px",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            width: "2px",
                            height: `${
                              72 +
                              (labelPositions[
                                vehicle.deviceId || vehicle.device_id
                              ] || 0)
                            }px`,
                            top: "10px",
                            backgroundColor: getBackgroundColor(
                              vehicle.deviceId || vehicle.device_id,
                            ),
                            bottom: "9px",
                            left: "50%",
                            transform: "translateX(-50%)",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "-10px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            color: "rgb(0, 0, 0)",
                            fontSize: "10px",
                            fontWeight: 600,
                            fontFamily: "Arial, sans-serif",
                            lineHeight: "14.06px",
                            padding: "8px",
                            textAlign: "left",
                            whiteSpace: "nowrap",
                            backgroundColor: getBackgroundColor(
                              vehicle.deviceId || vehicle.device_id,
                            ),
                            borderRadius: "12px",
                            zIndex: 1000,
                            border: "1px solid black",
                          }}
                        >
                          {vehicleName[vehicle.deviceId] ||
                            vehicleName[vehicle.device_id] ||
                            "---"}
                        </div>
                      </div>
                    </OverlayView>
                    <Marker
                      position={{
                        lat: parseFloat(vehicle.latitude || vehicle.Latitude),
                        lng: parseFloat(vehicle.longitude || vehicle.Longitude),
                      }}
                      icon={{
                        url: BusIcon,
                        scaledSize: new window.google.maps.Size(30, 30),
                        anchor: new window.google.maps.Point(15, 15),
                      }}
                    />
                  </React.Fragment>
                ),
            )}
        </GoogleMap>
      </div>
      <div style={{ width: "28%" }}>
        <ScreenRouteVehicleStatusTable
          loading={loading}
          error={error}
          vehicleData={vehicleData}
          formatDistance={formatDistance}
          formatTime={formatTime}
          vehicleLocations={vehicleLocations}
        />
      </div>
    </div>
  );
};

export default RouteScreen;
